/**
 * Sales line prices — who may see them, and how to add them up.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │  CHANGING WHO SEES SALES PRICES IS maySeeSalesPrices, HERE AND IN         │
 * │  api/src/sales/sales-price-visibility.js. Both, or the UI renders a field │
 * │  the API refuses to save (or worse, hides one it happily returns).        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The API is the real gate: it strips prices from the response body, so a
 * tier-1 user never receives one regardless of what this file does. This exists
 * so their UI does not show an input that silently discards what they type.
 *
 * ── WHY PRICES ARE TEXT ─────────────────────────────────────────────────────
 * `unit_price_text` is free text (migration 022) until ERP chunk 4 brings real
 * money columns. That means a price may be "$1,500.00", "1500", or "TBD" — and
 * "TBD" is a legitimate state of a deal, not bad data.
 *
 * So: **display what was typed, verbatim.** Only the derived amount and total
 * parse, and they say so when they cannot.
 */

import type { ItemType } from './itemTypes';

export interface PricedUser {
  can_sales?: boolean;
  can_financial?: boolean;
  can_admin?: boolean;
  isadmin?: boolean;
}

export interface PricedLine {
  item_type: string;
  quantity: number;
  unit_price_text?: string | null;
}

/**
 * May this user see and enter sales prices?
 *
 * Mirrors the API predicate exactly, including the admin flags — this is a
 * tier-2 rule and tier 2 is admin-bypassed. Deliberately NOT `hasCapability`,
 * which checks neither `can_financial` nor the legacy `isadmin` and would
 * therefore disagree with the server on two kinds of user.
 */
export const maySeeSalesPrices = (user: PricedUser | null | undefined): boolean => {
  const u = user || {};
  return !!(u.can_sales || u.can_financial || u.can_admin || u.isadmin);
};

/**
 * The number inside a typed price, or null when there isn't one.
 *
 * Tolerates the shapes people actually type: `$1,500.00`, `1500`, `1,500`.
 * Returns null for `TBD`, `per contract`, a range, or anything else that is not
 * one number — null means "cannot be totaled", never "zero".
 */
export function parsePrice(text: string | null | undefined): number | null {
  if (text == null) return null;
  const cleaned = String(text).replace(/[$,\s]/g, '');
  if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** `$1,500.00`. Matches the quote documents these numbers are transcribed from. */
export const formatMoney = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** quantity × unit price, or null when the price cannot be parsed. */
export function lineAmount(line: PricedLine): number | null {
  const unit = parsePrice(line.unit_price_text);
  if (unit == null) return null;
  return unit * (line.quantity || 0);
}

export interface OrderTotal {
  /** Sum of every line that could be parsed. */
  total: number;
  /** Lines carrying a price that is not a number ("TBD"). The total excludes them. */
  unparseable: number;
  /** Lines with no price entered at all. Also excluded. */
  unpriced: number;
  /** True when every line contributed, so the total is the whole order. */
  complete: boolean;
}

/**
 * Add an order up, and be honest about what was left out.
 *
 * A quote where one line says "TBD" has no true total, and printing a confident
 * number for the rest would be a lie by omission. The caller gets the partial sum
 * plus the counts needed to caveat it.
 */
export function orderTotal(lines: PricedLine[]): OrderTotal {
  let total = 0;
  let unparseable = 0;
  let unpriced = 0;

  for (const line of lines) {
    const raw = line.unit_price_text;
    if (raw == null || String(raw).trim() === '') {
      unpriced += 1;
      continue;
    }
    const amount = lineAmount(line);
    if (amount == null) {
      unparseable += 1;
      continue;
    }
    total += amount;
  }

  return { total, unparseable, unpriced, complete: unparseable === 0 && unpriced === 0 };
}

/**
 * A one-line caveat for the total, or null when none is needed.
 *
 * Copy lives here rather than in the component so the two surfaces that show a
 * total cannot word it differently.
 */
export function totalCaveat(t: OrderTotal): string | null {
  const parts: string[] = [];
  if (t.unpriced > 0) parts.push(`${t.unpriced} line${t.unpriced === 1 ? '' : 's'} not priced`);
  if (t.unparseable > 0) parts.push(`${t.unparseable} without a number`);
  return parts.length ? `Excludes ${parts.join(', ')}.` : null;
}

/** Placeholder hint for a price input, tuned to what the item usually is. */
export const pricePlaceholder = (type: ItemType | undefined): string =>
  type?.kind === 'charge' ? 'Total' : 'Per unit';
